import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { API_URL } from "../config";
import { getAccessToken, apiFetch } from '../utils/auth';

const RoleStateCtx = createContext({
  role: null,           // для обратной совместимости = roleCode
  roleId: null,
  roleCode: null,
  roleName: null,
  permissions: [],
  employeeId: null,
  me: null,
  loading: true,
});

const RoleActionsCtx = createContext({
  refresh: async () => {},
  hasPermission: () => false,
});

export function RoleProvider({ children }) {
  const [state, setState] = useState({
    role: null,         // для обратной совместимости = roleCode
    roleId: null,
    roleCode: null,
    roleName: null,
    permissions: [],
    employeeId: null,
    me: null,
    loading: true,
  });

  const refresh = useCallback(async () => {
    const token = getAccessToken && getAccessToken();
    if (!token) {
      setState({
        role: null,
        roleId: null,
        roleCode: null,
        roleName: null,
        permissions: [],
        employeeId: null,
        me: null,
        loading: false,
      });
      return;
    }

    try {
      // Используем apiFetch — он сам обновит access по refresh и
      // повторит запрос при необходимости.
      const res = await apiFetch(`${API_URL}/users/me`);
      if (!res.ok) throw new Error("Failed /users/me");

      const me = await res.json();

      const roleCode = me?.role_code ?? null;
      const roleId = me?.role_id ?? null;
      const roleName = me?.role_name ?? null;
      const permissions = Array.isArray(me?.permissions) ? me.permissions : [];
      const employeeId = me?.employee_id ?? null;

      setState({
        role: roleCode, // оставляем старое поле для совместимости со старым кодом
        roleId,
        roleCode,
        roleName,
        permissions,
        employeeId,
        me,
        loading: false,
      });

      // на всякий случай синхронизируем с localStorage
      localStorage.setItem("role", roleCode ?? "");
      localStorage.setItem("role_code", roleCode ?? "");
      localStorage.setItem("role_id", roleId != null ? String(roleId) : "");
      localStorage.setItem("role_name", roleName ?? "");
      localStorage.setItem("employee_id", employeeId != null ? String(employeeId) : "");
      localStorage.setItem("permissions", JSON.stringify(permissions));
    } catch {
      setState({
        role: null,
        roleId: null,
        roleCode: null,
        roleName: null,
        permissions: [],
        employeeId: null,
        me: null,
        loading: false,
      });
    }
  }, []);

  const hasPermission = useCallback((code) => {
    if (!code) return false;
    return Array.isArray(state.permissions) && state.permissions.includes(code);
  }, [state.permissions]);

  useEffect(() => {
    refresh();

    // подхватываем смену токена из других вкладок
    const onStorage = (e) => {
      if (e.key === "access_token") refresh();
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [refresh]);

  const actionsValue = useMemo(() => ({
    refresh,
    hasPermission,
  }), [refresh, hasPermission]);

  return (
    <RoleStateCtx.Provider value={{ ...state }}>
      <RoleActionsCtx.Provider value={actionsValue}>
        {children}
      </RoleActionsCtx.Provider>
    </RoleStateCtx.Provider>
  );
}

export const useRoleState = () => useContext(RoleStateCtx);          // { role, roleId, roleCode, roleName, permissions, employeeId, me, loading }
export const useRole = () => useContext(RoleStateCtx).role;          // для обратной совместимости: roleCode
export const useRoleCode = () => useContext(RoleStateCtx).roleCode;
export const useRoleId = () => useContext(RoleStateCtx).roleId;
export const useRoleName = () => useContext(RoleStateCtx).roleName;
export const usePermissions = () => useContext(RoleStateCtx).permissions || [];
export const useEmployeeId = () => useContext(RoleStateCtx).employeeId;
export const useMe = () => useContext(RoleStateCtx).me;
export const useRoleActions = () => useContext(RoleActionsCtx);
export const useHasPermission = () => useContext(RoleActionsCtx).hasPermission;